import React, { useEffect, useRef, useState } from "react";
import "../styles/components/SharePostModal.css";
import { useTranslation } from "react-i18next";
import API_BASE, { withApiCredentials } from "../api";
import { trackProductEvent } from "../analytics/trackEvent";
import { buildPostsById, resolveRootPost } from "../utils/postShares";

function getSharedSourcePost(post, posts = []) {
  const postsById = buildPostsById(posts);
  return resolveRootPost(post, postsById) || post?.sharedPost || post;
}

function getRecipientCountBucket(count) {
  if (count <= 0) return "0";
  if (count === 1) return "1";
  if (count <= 3) return "2-3";
  if (count <= 10) return "4-10";
  return "11+";
}

export default function SharePostModal({ post, posts = [], onClose }) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [messageLoading, setMessageLoading] = useState(false);
  const [friendsLoading, setFriendsLoading] = useState(false);
  const [messagePickerOpen, setMessagePickerOpen] = useState(false);
  const [friends, setFriends] = useState([]);
  const [friendQuery, setFriendQuery] = useState("");
  const [selectedFriendIds, setSelectedFriendIds] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const closeTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const sharedSourcePost = getSharedSourcePost(post, posts);
  const sharedPreviewAuthor =
    sharedSourcePost?.user || post?.sharedPost?.user || post?.user || "Unknown";
  const sharedPreviewCaption = (
    sharedSourcePost?.caption ||
    post?.sharedPost?.caption ||
    post?.caption ||
    ""
  ).trim();

  const filteredFriends = friends.filter((friend) =>
    (friend?.username || "")
      .toLowerCase()
      .includes(friendQuery.trim().toLowerCase())
  );

  const toggleFriendSelection = (friendId) => {
    setSelectedFriendIds((prev) =>
      prev.includes(friendId)
        ? prev.filter((id) => id !== friendId)
        : [...prev, friendId]
    );
  };

  const handleShareToFeed = async () => {
    const token = localStorage.getItem("token");
    if (loading) return;

    try {
      setLoading(true);

      const fd = new FormData();
      if (token) {
        fd.append("token", token);
      }
      fd.append("post_id", post.id);
      fd.append("text", text);

      const res = await fetch(
        `${API_BASE}/posts/share`,
        withApiCredentials({
          method: "POST",
          body: fd,
        })
      );

      if (!res.ok) throw new Error("share failed");

      const data = await res.json();

      window.dispatchEvent(
        new CustomEvent("postShared", {
          detail: { post: data.post },
        })
      );
      trackProductEvent("post_shared_to_feed", {
        featureArea: "feed",
        metadata: {
          source: "share_modal",
          post_type: sharedSourcePost?.postType || post?.postType || "post",
          is_shared_post: Boolean(post?.sharedPost),
        },
      });

      onClose();
    } catch (err) {
      console.error("share to feed failed:", err);
      alert(t("share_post_failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenMessagePicker = async () => {
    const token = localStorage.getItem("token");
    if (loading || messageLoading || friendsLoading) return;

    try {
      setFriendsLoading(true);

      const [meRes, usersRes] = await Promise.all([
        fetch(
          `${API_BASE}/auth/me`,
          withApiCredentials({
            headers: token
              ? {
                  Authorization: `Bearer ${token}`,
                }
              : {},
          })
        ),
        fetch(`${API_BASE}/auth/users`),
      ]);

      if (!meRes.ok || !usersRes.ok) {
        throw new Error("failed to load share recipients");
      }

      const me = await meRes.json();
      const users = await usersRes.json();
      const usersById = {};

      users.forEach((user) => {
        if (user?.id) {
          usersById[user.id] = user;
        }
      });

      const friendUsers = (me?.friends || [])
        .map((friendId) => usersById[friendId])
        .filter(Boolean);

      setFriends(friendUsers);
      setFriendQuery("");
      setSelectedFriendIds([]);
      setMessagePickerOpen(true);
    } catch (err) {
      console.error("share as message failed:", err);
      alert(
        t("share_message_picker_failed", {
          defaultValue: "Failed to load friends.",
        })
      );
    } finally {
      setFriendsLoading(false);
    }
  };

  const handleSendAsMessage = async () => {
    const token = localStorage.getItem("token");
    if (messageLoading) return;

    if (selectedFriendIds.length === 0) {
      alert(
        t("share_message_pick_friend", {
          defaultValue: "Select at least one friend.",
        })
      );
      return;
    }

    try {
      setMessageLoading(true);
      const sharedMessage = text.trim();

      for (const friendId of selectedFriendIds) {
        const fd = new FormData();
        if (token) {
          fd.append("token", token);
        }
        fd.append("to_user_id", friendId);
        fd.append("text", sharedMessage);

        if (sharedSourcePost?.id) {
          fd.append("shared_post_id", sharedSourcePost.id);

          if (sharedSourcePost?.userId) {
            fd.append("shared_post_user_id", sharedSourcePost.userId);
          }

          if (sharedSourcePost?.user) {
            fd.append("shared_post_user", sharedSourcePost.user);
          }

          if (sharedSourcePost?.caption) {
            fd.append("shared_post_caption", sharedSourcePost.caption);
          }

          if (sharedSourcePost?.mediaUrl) {
            fd.append("shared_post_media_url", sharedSourcePost.mediaUrl);
          }

          if (sharedSourcePost?.postDate) {
            fd.append("shared_post_post_date", String(sharedSourcePost.postDate));
          }
        }

        const res = await fetch(`${API_BASE}/messages/send`, withApiCredentials({
          method: "POST",
          body: fd,
        }));

        if (!res.ok) {
          throw new Error("share message send failed");
        }
      }

      setSuccessMessage(
        t("share_message_sent", {
          defaultValue: "Post sent in message.",
        })
      );
      trackProductEvent("message_sent", {
        featureArea: "messages",
        metadata: {
          source: "share_post_modal",
          has_shared_post: Boolean(sharedSourcePost?.id),
          recipient_count_bucket: getRecipientCountBucket(selectedFriendIds.length),
        },
      });
      closeTimeoutRef.current = window.setTimeout(() => {
        onClose();
      }, 1100);
    } catch (err) {
      console.error("share as message failed:", err);
      alert(
        t("share_message_send_failed", {
          defaultValue: "Failed to send post in message.",
        })
      );
    } finally {
      setMessageLoading(false);
    }
  };

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      <div className="share-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{t("share")}</h3>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("share_add_text")}
        />

        <div className="share-preview">
          <strong>{sharedPreviewAuthor}</strong>
          <p>{sharedPreviewCaption}</p>
        </div>

        {successMessage ? (
          <div
            className="share-feedback share-feedback--success"
            role="status"
            aria-live="polite"
          >
            <strong>{t("success", { defaultValue: "Success" })}</strong>
            <span>{successMessage}</span>
          </div>
        ) : null}

        {messagePickerOpen && (
          <div className="share-message-picker">
            <div className="share-message-picker-header">
              <strong>
                {t("share_as_message", { defaultValue: "Send as Message" })}
              </strong>
              <span>
                {t("share_message_picker_subtitle", {
                  defaultValue: "Select one or more friends.",
                })}
              </span>
            </div>

            <input
              type="text"
              value={friendQuery}
              onChange={(e) => setFriendQuery(e.target.value)}
              placeholder={t("share_search_friend")}
              className="share-friend-search"
            />

            <div className="share-friend-list">
              {filteredFriends.map((friend) => {
                const checked = selectedFriendIds.includes(friend.id);

                return (
                  <label key={friend.id} className="share-friend-item">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleFriendSelection(friend.id)}
                    />
                    <img
                      src={
                        friend.avatar
                          ? `${API_BASE}${friend.avatar}`
                          : "https://cdn-icons-png.flaticon.com/512/847/847969.png"
                      }
                      alt={friend.username || t("profile_picture")}
                      className="share-friend-avatar"
                    />
                    <span>{friend.username}</span>
                  </label>
                );
              })}

              {filteredFriends.length === 0 && (
                <p className="share-friend-empty">
                  {friends.length === 0
                    ? t("no_friends")
                    : t("no_results", { defaultValue: "No results found" })}
                </p>
              )}
            </div>
          </div>
        )}

        <div className="share-actions">
          {messagePickerOpen ? (
            <>
              <button
                className="primary"
                onClick={handleSendAsMessage}
                disabled={loading || messageLoading || friendsLoading || Boolean(successMessage)}
              >
                {messageLoading ? t("loading") : t("send")}
              </button>

              <button
                className="secondary"
                onClick={() => setMessagePickerOpen(false)}
                disabled={loading || messageLoading || friendsLoading || Boolean(successMessage)}
              >
                {t("back", { defaultValue: "Back" })}
              </button>

              <button
                className="cancel"
                onClick={onClose}
                disabled={loading || messageLoading || friendsLoading || Boolean(successMessage)}
              >
                {t("cancel")}
              </button>
            </>
          ) : (
            <>
              <button
                className="primary"
                onClick={handleShareToFeed}
                disabled={loading || messageLoading || friendsLoading || Boolean(successMessage)}
              >
                {loading ? t("sharing") : t("share_to_feed")}
              </button>

              <button
                className="secondary"
                onClick={handleOpenMessagePicker}
                disabled={loading || messageLoading || friendsLoading || Boolean(successMessage)}
              >
                {friendsLoading ? t("loading") : t("share_as_message")}
              </button>

              <button
                className="cancel"
                onClick={onClose}
                disabled={loading || messageLoading || friendsLoading || Boolean(successMessage)}
              >
                {t("cancel")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
